import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { getRudderstackAnonymousIdWithTimeout } from '@/hooks/useRudderstackAnalytics'
import { apiErrorMessage, ApiErrorShape, safeJson, SERVER_BUSY_MESSAGE } from '@/utils/apiResponse'
import { brand } from '@/utils/brand'
import { getV5ApiUrl } from '@/utils/urlHelpers'

// Abort the request before CloudFront's 30s origin timeout so the UI fails fast
// with a friendly message instead of hanging on a stalled request.
const CREATE_USER_TIMEOUT_MS = 25_000

interface AuthDataPayload {
  provider: string
  identity_token: string
}

export interface UserCreatePayload {
  first_name: string
  last_name: string
  password?: string
  password_confirmation?: string
  country_code: string
  sms_tracking_optin?: boolean | undefined
  requested_club?: string | undefined
  hear_about_us?: string | undefined
  other_hear_about_us?: string | undefined
  terms: boolean
  mobile_verified?: boolean | undefined
  email: string
  phone_number: string
  brand_id: string
  auth_data?: AuthDataPayload
}

export type UserCreateResponse = {
  auth_token: string
  id: string
  brand_id: string
  country: string
  email: string
  first_name: string
  hear_about_us: string
  other_hear_about_us: string
  last_name: string
  phone_number: string
  sms_tracking_optin: boolean
  mobile_verified: boolean
  terms: boolean
  roles?: string[]
}

const createUser = async (user: UserCreatePayload): Promise<UserCreateResponse> => {
  const usersUrl = getV5ApiUrl('/users')

  // The backend reads top-level anonymous_id for its server-side sign_up event
  // (Api::V5::UsersController#post_signup_context) so it carries the same
  // RudderStack id as the browser SDK. Bounded wait, taken before the request
  // timer starts so it never eats into the 25s budget.
  const anonymousId = await getRudderstackAnonymousIdWithTimeout(1000)

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), CREATE_USER_TIMEOUT_MS)

  let response: Response
  try {
    response = await fetch(usersUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-brand-id': brand.key },
      body: JSON.stringify({
        user,
        frontend_app_booking_flow: true,
        ...(anonymousId ? { anonymous_id: anonymousId } : {}),
      }),
      signal: controller.signal,
    })
  } catch {
    // AbortError (client timeout) or a network failure.
    throw new Error(SERVER_BUSY_MESSAGE)
  } finally {
    clearTimeout(timeout)
  }

  // safeJson never throws on a non-JSON body (e.g. a CloudFront HTML timeout
  // page), so the raw `Unexpected token '<'` parse crash can no longer happen.
  const data = await safeJson<UserCreateResponse & ApiErrorShape>(response)

  if (!response.ok) {
    throw new Error(apiErrorMessage(data))
  }

  return data
}

const useCreateUser = (): UseMutationResult<
  UserCreateResponse,
  Error,
  UserCreatePayload,
  unknown
> => {
  return useMutation<UserCreateResponse, Error, UserCreatePayload>({
    mutationFn: createUser,
  })
}

export default useCreateUser
